import { useState } from "react";
import { Plus } from "lucide-react";
import { api, getApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export function AddCustomerDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    mobile: "",
    password: "",
    initial_balance: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/customers", {
        name: form.name.trim(),
        mobile: form.mobile.trim(),
        password: form.password,
        initial_balance: Number(form.initial_balance || 0),
      });
      toast.success(`Customer ${data.name} created`);
      setForm({ name: "", mobile: "", password: "", initial_balance: "" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="admin-add-customer-btn"
          className="bg-slate-900 hover:bg-slate-800 gap-2"
        >
          <Plus className="h-4 w-4" /> Add customer
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="add-customer-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">New customer</DialogTitle>
          <DialogDescription>
            Create a customer account. They can sign in with their mobile and password.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label className="label-overline">Full name</Label>
            <Input
              data-testid="new-customer-name"
              value={form.name}
              onChange={set("name")}
              required
              maxLength={80}
            />
          </div>
          <div className="space-y-2">
            <Label className="label-overline">Mobile number</Label>
            <Input
              data-testid="new-customer-mobile"
              value={form.mobile}
              onChange={(e) =>
                setForm({ ...form, mobile: e.target.value.replace(/[^0-9+]/g, "") })
              }
              inputMode="numeric"
              required
              className="font-mono-num"
            />
          </div>
          <div className="space-y-2">
            <Label className="label-overline">Password</Label>
            <Input
              data-testid="new-customer-password"
              type="password"
              value={form.password}
              onChange={set("password")}
              required
              minLength={4}
            />
          </div>
          <div className="space-y-2">
            <Label className="label-overline">Opening balance (₹)</Label>
            <Input
              data-testid="new-customer-balance"
              type="number"
              min="0"
              step="0.01"
              value={form.initial_balance}
              onChange={set("initial_balance")}
              className="font-mono-num"
              placeholder="0.00"
            />
          </div>
          <DialogFooter>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="submit-new-customer"
              className="bg-slate-900 hover:bg-slate-800"
            >
              {submitting ? "Creating…" : "Create customer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
